import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { TraitSelector } from './TraitSelector';
import { WaifuEquipment } from './WaifuEquipment';
import {
  useAllocateStat,
  useEquipment,
  useLevelTable,
  useUseItem,
  useWaifus,
} from '@/api/queries';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { imageUrl } from '@/api/client';
import { hapticNotify } from '@/lib/telegram';
import { cn } from '@/lib/cn';
import { hpFillClass } from '@/lib/hp';
import type { StatName } from '@/types';

export function WaifuDetailOverlay() {
  const userId = useAuthStore((s) => s.userId) ?? 0;
  const selectedWaifuId = useUIStore((s) => s.selectedWaifuId);
  const setSelectedWaifuId = useUIStore((s) => s.setSelectedWaifuId);
  const pushNotification = useUIStore((s) => s.pushNotification);
  const openModal = useUIStore((s) => s.openModal);

  const { data, isLoading } = useWaifus(userId);
  const waifus = data?.waifus ?? [];
  const idx = waifus.findIndex((w) => w.id === selectedWaifuId);
  const waifu = idx >= 0 ? waifus[idx] : null;

  const { data: equipmentData } = useEquipment(waifu?.id, userId);
  const bonuses = equipmentData?.bonuses ?? {
    strength: 0,
    dexterity: 0,
    intelligence: 0,
    hp: 0,
  };
  const { getNextLevelXp } = useLevelTable();

  const allocate = useAllocateStat(waifu?.id ?? 0, userId);
  const useItem = useUseItem(userId);

  const [traitSelectorOpen, setTraitSelectorOpen] = useState(false);
  const [portraitZoomed, setPortraitZoomed] = useState(false);

  // Close the fullscreen portrait when switching to another waifu.
  useEffect(() => setPortraitZoomed(false), [selectedWaifuId]);

  useEffect(() => {
    if (!waifu) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' && idx < waifus.length - 1) {
        setSelectedWaifuId(waifus[idx + 1].id);
      } else if (e.key === 'ArrowLeft' && idx > 0) {
        setSelectedWaifuId(waifus[idx - 1].id);
      } else if (e.key === 'Escape') {
        setSelectedWaifuId(null);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [waifu, idx, waifus, setSelectedWaifuId]);

  if (selectedWaifuId === null) return null;
  if (isLoading || !waifu) return null;

  const allocStat = async (stat: StatName) => {
    if (waifu.stat_points <= 0) return;
    try {
      await allocate.mutateAsync(stat);
      hapticNotify('success');
    } catch (err) {
      hapticNotify('error');
      const message =
        err instanceof Error ? err.message : 'Не удалось распределить очко';
      pushNotification({ type: 'error', message });
    }
  };

  const triggerLimitBreaker = async () => {
    try {
      const res = await useItem.mutateAsync({
        item_id: 'limit_breaker',
        waifu_id: waifu.id,
      });
      if (res.success) {
        hapticNotify('success');
        pushNotification({
          type: 'success',
          message: res.message ?? `Лимит сломан! Новый ранг: ${res.new_rarity ?? '?'}`,
        });
      } else {
        pushNotification({ type: 'error', message: res.error ?? 'Не удалось' });
      }
    } catch (err) {
      hapticNotify('error');
      const message = err instanceof Error ? err.message : 'Limit breaker недоступен';
      pushNotification({ type: 'error', message });
    }
  };

  const expMax = Math.max(
    1,
    waifu.next_level_xp ?? getNextLevelXp(waifu.level) ?? 1,
  );
  const totalMaxHp = (waifu.max_hp ?? 0) + (bonuses.hp ?? 0);
  const hpFill = hpFillClass(waifu.current_hp, totalMaxHp);

  const statIcons: Record<StatName, string> = {
    strength: '⚔️',
    dexterity: '🏃',
    intelligence: '📚',
  };

  return (
    <div
      className="fixed inset-0 z-40 bg-bg flex flex-col animate-fade-in"
      style={{
        paddingTop:
          'max(env(safe-area-inset-top, 0px), var(--tg-content-safe-area-top, 0px), var(--tg-safe-area-top, 0px))',
      }}
    >
      <header className="flex items-center justify-between px-4 py-3 border-b border-white/5 shrink-0">
        <button
          onClick={() => setSelectedWaifuId(null)}
          className="text-white/60 hover:text-white text-2xl leading-none w-9 h-9 inline-flex items-center justify-center rounded-lg hover:bg-white/5"
        >
          ←
        </button>
        <h2 className="font-display text-lg text-white truncate">{waifu.name}</h2>
        <div className="flex gap-1">
          <button
            disabled={idx === 0}
            onClick={() => idx > 0 && setSelectedWaifuId(waifus[idx - 1].id)}
            className="w-9 h-9 rounded-lg hover:bg-white/5 disabled:opacity-30 text-white"
          >
            ‹
          </button>
          <button
            disabled={idx === waifus.length - 1}
            onClick={() =>
              idx < waifus.length - 1 && setSelectedWaifuId(waifus[idx + 1].id)
            }
            className="w-9 h-9 rounded-lg hover:bg-white/5 disabled:opacity-30 text-white"
          >
            ›
          </button>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div className="flex gap-4">
          <div
            className={cn(
              'w-32 h-40 rounded-2xl overflow-hidden bg-bg-elevated shrink-0 ring-2 ring-accent/40',
              waifu.portrait_url && 'cursor-zoom-in',
            )}
            role={waifu.portrait_url ? 'button' : undefined}
            tabIndex={waifu.portrait_url ? 0 : undefined}
            onClick={() => waifu.portrait_url && setPortraitZoomed(true)}
          >
            {waifu.portrait_url ? (
              <img
                src={imageUrl(waifu.portrait_url) ?? ''}
                alt={waifu.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-4xl text-white">
                {waifu.name.charAt(0)}
              </div>
            )}
          </div>
          <div className="flex-1 space-y-2.5 min-w-0">
            {/* Top row: race · class · character + Lv badge */}
            <div className="flex items-center justify-between gap-2">
              <div className="text-[11px] text-white/50 uppercase tracking-wider truncate min-w-0">
                {waifu.race_name && <span>{waifu.race_name}</span>}
                {waifu.class_name && <span> · {waifu.class_name}</span>}
                {waifu.character_name && <span> · {waifu.character_name}</span>}
              </div>
              <span className="shrink-0 px-2 py-0.5 rounded-md bg-accent/20 text-accent text-[11px] font-semibold tabular-nums">
                Lv.{waifu.level}
              </span>
            </div>

            <StatBar
              label="Опыт"
              current={waifu.experience ?? 0}
              max={expMax}
              fillClass="bg-accent"
            />

            <StatBar
              label="Здоровье"
              current={waifu.current_hp}
              max={totalMaxHp}
              bonus={bonuses.hp}
              fillClass={hpFill}
            />

            <div className="flex items-center justify-between gap-2">
              <div className="text-pink-300 text-sm">
                ♥ {waifu.relationship} / 100
              </div>
              <button
                onClick={() => openModal('relationships')}
                className="px-2.5 py-1 rounded-lg bg-bg-elevated border border-white/10 text-[11px] text-white/80 hover:border-white/25 active:scale-95 transition shrink-0"
              >
                Отношения
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <h3 className="text-sm uppercase tracking-wide text-white/40 font-semibold">
            Характеристики
            {waifu.stat_points > 0 && (
              <span className="ml-2 text-accent text-[11px]">
                +{waifu.stat_points} доступно
              </span>
            )}
          </h3>
          <div className="grid grid-cols-3 gap-2">
            {(['strength', 'dexterity', 'intelligence'] as StatName[]).map(
              (stat) => {
                const bonus = bonuses[stat] ?? 0;
                return (
                  <button
                    key={stat}
                    disabled={waifu.stat_points <= 0}
                    onClick={() => allocStat(stat)}
                    className={cn(
                      'p-3 rounded-xl bg-bg-elevated border text-center transition active:scale-[0.98]',
                      waifu.stat_points > 0
                        ? 'border-accent/40 hover:bg-bg-muted'
                        : 'border-white/5 opacity-90 cursor-not-allowed',
                    )}
                  >
                    <div className="text-base leading-none">{statIcons[stat]}</div>
                    <div className="mt-1 text-xl font-display text-white tabular-nums">
                      {waifu[stat]}
                      {bonus > 0 && (
                        <span className="text-success text-sm font-medium ml-1">
                          +{bonus}
                        </span>
                      )}
                    </div>
                    {waifu.stat_points > 0 && (
                      <div className="text-[10px] text-accent mt-0.5">+1</div>
                    )}
                  </button>
                );
              },
            )}
          </div>
        </div>

        <div className="space-y-2">
          <h3 className="text-sm uppercase tracking-wide text-white/40 font-semibold">
            Трейты
            {waifu.trait_points > 0 && (
              <span className="ml-2 text-accent text-[11px]">
                +{waifu.trait_points} доступно
              </span>
            )}
          </h3>
          <div className="flex flex-wrap gap-2">
            {waifu.traits.map((trait) => (
              <span
                key={trait.id}
                className="px-3 py-1 rounded-full bg-bg-elevated border border-white/10 text-xs text-white/80"
              >
                {trait.name}
              </span>
            ))}
            {waifu.trait_points > 0 && (
              <button
                onClick={() => setTraitSelectorOpen(true)}
                className="px-3 py-1 rounded-full bg-accent/15 border border-accent/40 text-xs text-accent"
              >
                + Выбрать
              </button>
            )}
          </div>
        </div>

        <WaifuEquipment waifu={waifu} userId={userId} />

        <Button
          fullWidth
          variant="secondary"
          onClick={triggerLimitBreaker}
          disabled={useItem.isPending}
        >
          {useItem.isPending ? 'Прорыв…' : '🔓 Limit Breaker'}
        </Button>
      </div>

      <TraitSelector
        open={traitSelectorOpen}
        onClose={() => setTraitSelectorOpen(false)}
        waifuId={waifu.id}
        userId={userId}
        excludeTraitIds={new Set(waifu.traits.map((t) => t.id))}
      />

      {portraitZoomed && waifu.portrait_url && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 cursor-zoom-out animate-fade-in"
          style={{ touchAction: 'none' }}
          onClick={() => setPortraitZoomed(false)}
        >
          <img
            src={imageUrl(waifu.portrait_url) ?? ''}
            alt={waifu.name}
            className="max-w-full max-h-full object-contain select-none rounded-2xl"
          />
        </div>
      )}
    </div>
  );
}

function StatBar({
  label,
  current,
  max,
  bonus,
  fillClass,
}: {
  label: string;
  current: number;
  max: number;
  bonus?: number;
  fillClass: string;
}) {
  const safeMax = Math.max(1, max);
  const percent = Math.max(0, Math.min(100, (current / safeMax) * 100));
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-[11px]">
        <span className="text-white/70 font-semibold">{label}</span>
        <span className="tabular-nums text-white/80">
          {current} / {max}
          {bonus && bonus > 0 ? (
            <span className="text-success ml-1">(+{bonus})</span>
          ) : null}
        </span>
      </div>
      <div className="h-2 rounded-full bg-black/40 overflow-hidden">
        <div
          className={cn('h-full transition-[width] duration-300', fillClass)}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}
